/**
 * SendPaymentLinkDialog Component
 *
 * A dialog component for practitioners to send payment links to patients.
 * This component allows the practitioner to:
 * 1. Review the prescription details before sending
 * 2. Select a shipping carrier (UPS, FedEx, USPS, Ameriship)
 * 3. Choose a delivery option from the selected carrier
 * 4. Preview the total amount (medication + shipping)
 * 5. Send the payment link to the patient's email
 *
 * Key Flow:
 * - Practitioner opens dialog from prescription detail view
 * - Shipping carrier and option are selected by the practitioner
 * - Shipping selection is saved to the prescription record
 * - Payment link is sent to patient with pre-configured shipping
 * - Patient receives link with fixed shipping cost (cannot change)
 *
 * Free Shipment Handling:
 * - If patient's state qualifies for free shipment (global or clinic-specific),
 *   all shipping options show $0 cost
 * - Original cost is preserved and displayed with strikethrough
 * - Free shipment badge is shown to indicate the promotion
 *
 * @module SendPaymentLinkDialog
 */

import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogBody,
} from '@/components/ui/dialog';
import { AlertCircle, Loader2, Send, Package, ChevronDown, Truck, Gift, Pencil, Mail, Snowflake } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useClinic } from '@/contexts/ClinicContext';
import { useNavigate } from 'react-router-dom';
import { prescriptionService } from '@/services/prescriptionService';
import { STRIPE_ONBOARDING_TOAST } from '@/services/paymentService';
import { Prescription, ShippingCarrier, ShippingOption, ShippingSelection } from '@/lib/types';
import {
  shippingService,
  prescriptionMedicationRequestsToShippingLines,
} from '@/services/shippingService';
import { getDefaultContactValue } from '@/lib/utils';
import * as Accordion from '@radix-ui/react-accordion';
import cx from 'classnames';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import EditMedicationsDialog from '../clinic/EditMedicationsDialog';

/**
 * Props for the SendPaymentLinkDialog component
 */
interface SendPaymentLinkDialogProps {
    /** Whether the dialog is open/visible */
    open: boolean;
    /** Callback when dialog open state changes */
    onOpenChange: (open: boolean) => void;
    /** UUID of the prescription to send payment link for */
    prescription_id: string;
    /** Callback when payment link is successfully sent */
    onSuccess: () => void;
    /** Optional callback when prescription is updated (e.g., medications removed) */
    onPrescriptionUpdated?: () => void;
}

/**
 * SendPaymentLinkDialog component for sending payment links to patients
 *
 * @param {SendPaymentLinkDialogProps} props - Component props
 * @returns {JSX.Element | null} The dialog component or null if no prescription ID
 */
const SendPaymentLinkDialog: React.FC<SendPaymentLinkDialogProps> = ({
    open,
    onOpenChange,
    prescription_id,
    onSuccess,
    onPrescriptionUpdated,
}) => {
    // Loading and form state
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [prescription, setPrescription] = useState<Prescription | null>(null);
    const { clinic, isAdmin } = useClinic();
    const navigate = useNavigate();
    const paymentAccount = clinic?.paymentAccount;
    const onboardingComplete =
        !!paymentAccount?.stripe_account_id &&
        (paymentAccount?.stripe_charges_enabled !== false);
    const [show, setShow] = useState<string | null>(null);
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const [isError, setIsError] = useState(false);
    /** Custom email entered by the user when patient has no email on file */
    const [customEmail, setCustomEmail] = useState<string>('');
    // COMMENTED: Override option for price warning - payment link blocked when selling price <= base price
    // const [priceWarningAcknowledged, setPriceWarningAcknowledged] = useState(false);

    // ============================================
    // SHIPPING STATE
    // ============================================
    /** Available shipping carriers fetched from API */
    const [carriers, setCarriers] = useState<ShippingCarrier[]>([]);
    /** Currently selected carrier ID */
    const [selectedCarrierId, setSelectedCarrierId] = useState<string>('');
    /** Available delivery options for selected carrier */
    const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([]);
    /** Currently selected shipping option ID */
    const [selectedShippingOptionId, setSelectedShippingOptionId] = useState<string>('');
    /** Loading state for carriers fetch */
    const [isLoadingCarriers, setIsLoadingCarriers] = useState(false);
    /** Loading state for shipping options fetch */
    const [isLoadingShippingOptions, setIsLoadingShippingOptions] = useState(false);
    /** Whether patient qualifies for free shipment */
    const [isFreeShipment, setIsFreeShipment] = useState(false);
    /** Type of free shipment: 'global' or 'clinic_specific' */
    const [freeShipmentType, setFreeShipmentType] = useState<string | null>(null);
    /** Patient's state name for display purposes */
    const [patientState, setPatientState] = useState<string | null>(null);
    /** Error message for shipping options (shown inline) */
    const [shippingError, setShippingError] = useState<string | null>(null);
    /** Whether this order requires cold chain (overnight/express) shipping */
    const [requiresColdChain, setRequiresColdChain] = useState(false);
    const patientOverviewPath = prescription?.patient?.id
        ? isAdmin
            ? `/patients/${prescription.patient.id}`
            : `/practitioner-portal/patients/${prescription.patient.id}`
        : null;
    const shouldShowAddressLink = shippingError?.includes(
        'No delivery options available for this address'
    );
    /** Medication-only amount in dollars */
    const [medicationAmount, setMedicationAmount] = useState(0);
    /** Total amount in dollars (medication + shipping) */
    const [totalAmount, setTotalAmount] = useState(0);
    /** Total discount in cents */
    const [totalDiscountCents, setTotalDiscountCents] = useState(0);
    /** Subtotal before discount in cents */
    const [subtotalBeforeDiscountCents, setSubtotalBeforeDiscountCents] = useState(0);
    /** State for edit medications dialog */
    const [isEditMedicationsOpen, setIsEditMedicationsOpen] = useState(false);

    if (!prescription_id) return null;

    /**
     * Gets the current shipping selection object
     * @returns {ShippingSelection | null} The shipping selection or null if not fully selected
     */
    const getShippingSelection = (): ShippingSelection | null => {
        if (!selectedCarrierId || !selectedShippingOptionId) return null;
        
        const carrier = carriers.find(c => c.id === selectedCarrierId);
        const option = shippingOptions.find(o => o.id === selectedShippingOptionId);
        
        if (!carrier || !option) return null;
        
        return {
            carrierId: carrier.id,
            carrierName: carrier.name,
            optionId: option.id,
            optionName: option.name,
            cost: option.totalCost,
            originalCost: option.originalCost,
            isFreeShipment,
            freeShipmentType,
        };
    };

    const handleConfirm = async () => {
        const patientEmail = getDefaultContactValue(prescription?.patient?.contacts, 'email');
        const emailToUse = patientEmail || customEmail.trim();

        if (!patientEmail && !customEmail.trim()) {
            toast({
                title: 'Email required',
                description: 'Please enter a valid email address to send the payment link.',
                variant: 'destructive',
            });
            return;
        }

        if (!onboardingComplete) {
            toast({
                ...STRIPE_ONBOARDING_TOAST,
                variant: 'destructive',
                description: 'Complete Stripe Connect onboarding in Billing before sending payment links.',
            });
            return;
        }

        try {
            setIsSending(true);

            // First, save shipping selection to prescription
            const shippingSelection = getShippingSelection();
            if (shippingSelection) {
                const saveResult = await shippingService.saveShippingSelection(prescription_id, shippingSelection);
                if (!saveResult.success) {
                    toast({
                        title: 'Error',
                        description: saveResult.error || 'Failed to save shipping selection.',
                        variant: 'destructive',
                    });
                    return;
                }
            }

            // Send the payment link, passing custom email when patient has none on file
            await prescriptionService.sendPrescriptionLink(prescription_id, emailToUse !== patientEmail ? emailToUse : undefined);

            onSuccess();
            onOpenChange(false);
        } catch (error: any) {
            console.error(`Error sending payment link:`, error);
            toast({
                title: 'Error',
                description: error.message || `Failed to send payment link. Please try again.`,
                variant: 'destructive',
            });
        } finally {
            setIsSending(false);
        }
    };

    const fetchPrescription = async (silent = false) => {
        try {
            if (!silent) {
                setIsLoading(true);
            }
            const response = await prescriptionService.getPrescriptionById(clinic?.id, prescription_id);
            setPrescription(response.data.prescription);
            setMedicationAmount(response.data.total_amount || 0);
            setTotalAmount(response.data.total_amount || 0);
            setTotalDiscountCents(response.data.total_discount_cents || 0);
            setSubtotalBeforeDiscountCents(response.data.subtotal_before_discount || response.data.total_amount || 0);

        } catch (error: any) {
            console.error(`Error fetching prescription:`, error);
            toast({
                title: 'Error',
                description: error.message || `Failed to fetch prescription. Please try again.`,
            });
            return null;
        } finally {
            if (!silent) {
                setIsLoading(false);
            }
        }
    };

    /**
     * Fetches available shipping carriers from the API
     */
    const fetchCarriers = async () => {
        if (!clinic?.id) return;
        
        setIsLoadingCarriers(true);
        try {
            const result = await shippingService.getCarriers(clinic.id);
            if (result.success && result.data) {
                setCarriers(result.data);
            } else {
                console.error('Failed to fetch carriers:', result.error);
            }
        } catch (error) {
            console.error('Error fetching carriers:', error);
        } finally {
            setIsLoadingCarriers(false);
        }
    };

    /**
     * Handles carrier selection and fetches shipping options for the selected carrier
     * @param {string} carrierId - The selected carrier ID
     */
    const handleCarrierChange = async (carrierId: string) => {
        setSelectedCarrierId(carrierId);
        setSelectedShippingOptionId('');
        setShippingOptions([]);
        setIsFreeShipment(false);
        setFreeShipmentType(null);
        setRequiresColdChain(false);
        setShippingError(null);

        // Reset total to medication amount only
        setTotalAmount(medicationAmount);

        if (!carrierId || !clinic?.id || !prescription?.patient?.id) return;

        setIsLoadingShippingOptions(true);
        try {
            const result = await shippingService.calculateShippingCost(
                clinic.id,
                prescription.patient.id,
                carrierId,
                prescriptionMedicationRequestsToShippingLines(
                  prescription.medicationRequests || []
                )
            );

            if (result.success && result.data) {
                setShippingOptions(result.data);
                setIsFreeShipment(result.isFreeShipment);
                setFreeShipmentType(result.freeShipmentType || null);
                setPatientState(result.patientState || null);
                setRequiresColdChain(result.requiresColdChain);
                setShippingError(null);
                
                // Auto-select first option if available
                if (result.data.length > 0) {
                    handleShippingOptionChange(result.data[0].id, result.data);
                }
            } else {
                // Show error inline instead of toast
                setShippingError(result.error || 'Failed to calculate shipping cost. Please try a different carrier.');
            }
        } catch (error) {
            console.error('Error calculating shipping cost:', error);
            setShippingError('Failed to load shipping options. Please try again.');
        } finally {
            setIsLoadingShippingOptions(false);
        }
    };

    /**
     * Handles shipping option selection and updates the total amount
     * @param {string} optionId - The selected shipping option ID
     * @param {ShippingOption[]} options - Optional shipping options array (used for auto-selection)
     */
    const handleShippingOptionChange = (optionId: string, options?: ShippingOption[]) => {
        setSelectedShippingOptionId(optionId);
        
        const optionsToSearch = options || shippingOptions;
        const selectedOption = optionsToSearch.find(o => o.id === optionId);
        
        if (selectedOption) {
            // Add shipping cost to medication amount (convert dollars to cents)
            const shippingCents = Math.round(selectedOption.totalCost * 100);
            setTotalAmount(medicationAmount + shippingCents);
        } else {
            setTotalAmount(medicationAmount);
        }
    }

    useEffect(() => {
        if (prescription_id && open) {
            fetchPrescription();
            fetchCarriers();
            // Reset shipping state
            setSelectedCarrierId('');
            setSelectedShippingOptionId('');
            setShippingOptions([]);
            setIsFreeShipment(false);
            setFreeShipmentType(null);
            setRequiresColdChain(false);
            setPatientState(null);
            setShippingError(null);
            setCustomEmail('');
        }
    }, [prescription_id, open]);


    useEffect(() => {
        if (prescription) {
            let hasError = false;
            let errorMsg = '';

            let medicationRequests = prescription?.medicationRequests || [];
            // Check for missing products
            const missingProducts = medicationRequests.filter(
                medicationRequest => !medicationRequest.product_id
            );


            if (missingProducts.length > 0) {
                hasError = true;
                errorMsg = 'Please add a product to the Medication to continue';
                setIsError(true);
                setErrorMessages([errorMsg]);
                return;
            }

            for (let medicationRequest of medicationRequests) {
                let productDescription = medicationRequest?.product?.description;
                let basePrice = medicationRequest?.clinicProduct?.base_price_cents || (medicationRequest?.product?.price_per_unit || medicationRequest?.product?.price_plan_cost || 0) * 100 || 0;
                let sellingPrice = medicationRequest?.clinicProduct?.selling_price_cents || basePrice || 0;
                let errorMessage = '';

                if (!basePrice || basePrice <= 0) {
                    hasError = true;
                    errorMessage = `${productDescription} does not have pricing set. Please configure base and selling prices before proceeding.`;
                    setIsError(true);
                    setErrorMessages(prev => [...prev, errorMessage]);
                    continue;
                }

                if (!sellingPrice || sellingPrice <= 0) {
                    hasError = true;
                    errorMessage = `${productDescription} does not have pricing set. Please configure base and selling prices before proceeding.`;
                    setIsError(true);
                    setErrorMessages(prev => [...prev, errorMessage]);
                    continue;
                }

                // Block when selling price <= base price (clinic has no margin or loses money)
                if (sellingPrice <= basePrice) {
                    hasError = true;
                    errorMessage = `${productDescription} has a patient price that is less than or equal to the pharmacy cost. Please configure a greater patient price than the pharmacy cost before processing the payment.`;
                    setIsError(true);
                    setErrorMessages(prev => [...prev, errorMessage]);
                    continue;
                }
            }
        }
        return () => {
            setErrorMessages([]);
            setIsError(false);
        };
    }, [prescription]);

    // Detect when patient price is lower than or equal to pharmacy cost (clinic loses money or has no margin)
    const hasPriceWarning = (() => {
        const medicationRequests = prescription?.medicationRequests || [];
        for (const mr of medicationRequests) {
            const qty = Number(mr.quantity_authorized) || 1;
            const basePriceCents =
                mr.clinicProduct?.base_price_cents ??
                Math.round((mr.product?.price_per_unit || mr.product?.price_plan_cost || 0) * 100);
            const sellingPricePerUnit = mr.clinicProduct?.selling_price_cents ?? basePriceCents;
            const baseTotal = basePriceCents * qty;
            const sellingTotal =
                mr.selling_price_cents != null ? mr.selling_price_cents : sellingPricePerUnit * qty;
            if (baseTotal > 0 && sellingTotal <= baseTotal) return true;
        }
        return false;
    })();

    const handleClose = () => {
        if (!isLoading && !isSending) {
            onOpenChange(false);
        }
    };

    return (
        <>
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-brand-teal/10 rounded-full flex items-center justify-center">
                            <Send className="h-6 w-6 text-brand-teal" />
                        </div>
                        <div className="flex-1">
                            <DialogTitle className="text-[1.05rem] font-semibold">Send Payment Link</DialogTitle>
                            <DialogDescription className="text-[0.8rem] text-slate-500 mt-0">
                                Send a payment link to the patient for this prescription
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogBody className="py-4">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-full min-h-[200px]">
                            <Loader2 className="h-6 w-6 animate-spin text-brand-teal" />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Onboarding required — block sending payment link */}
                            {!onboardingComplete && (
                                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex flex-col gap-2">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="text-sm font-medium text-amber-800">
                                                {STRIPE_ONBOARDING_TOAST.title}
                                            </p>
                                            <p className="text-sm text-amber-700 mt-0.5">
                                                Complete Stripe Connect onboarding in Billing before sending payment links.
                                            </p>
                                        </div>
                                    </div>
                                    {isAdmin && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="self-start border-amber-300 text-amber-800 hover:bg-amber-100"
                                            onClick={() => navigate('/payments?tab=settings')}
                                        >
                                            Go to Billing
                                        </Button>
                                    )}
                                </div>
                            )}

                            {/* Prescription Summary */}
                            <div className="border border-slate-200 rounded-lg p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <h4 className="font-medium text-slate-900 text-sm">Order Summary</h4>
                                    <Button
                                        variant='ghost'
                                        size='sm'
                                        onClick={() => setIsEditMedicationsOpen(true)}
                                        className='h-8 px-3 text-xs text-slate-600 hover:text-slate-900'
                                    >
                                        <Pencil className='mr-1 h-3.5 w-3.5' />
                                        Edit
                                    </Button>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Patient:</span>
                                    <span className="font-medium text-slate-900 capitalize">
                                        {prescription?.patient?.full_name || '--'}
                                    </span>
                                </div>

                                {/* Email display / input */}
                                {(() => {
                                    const patientEmail = getDefaultContactValue(prescription?.patient?.contacts, 'email');
                                    return patientEmail ? (
                                        <div className="flex justify-between text-sm items-center">
                                            <span className="text-slate-500 flex items-center gap-1.5">
                                                <Mail className="h-3.5 w-3.5" />
                                                Email:
                                            </span>
                                            <span className="font-medium text-slate-900">{patientEmail}</span>
                                        </div>
                                    ) : (
                                        <div className="space-y-1.5">
                                            <label className="text-sm text-slate-500 flex items-center gap-1.5">
                                                <Mail className="h-3.5 w-3.5" />
                                                Patient Email <span className="text-red-500">*</span>
                                            </label>
                                            <Input
                                                type="email"
                                                placeholder="Enter patient email address"
                                                value={customEmail}
                                                onChange={e => setCustomEmail(e.target.value)}
                                                className="h-9 text-sm"
                                            />
                                            <p className="text-xs text-amber-600 flex items-center gap-1">
                                                <AlertCircle className="h-3 w-3 flex-shrink-0" />
                                                No email on file — enter one to send the payment link.
                                            </p>
                                        </div>
                                    );
                                })()}

                                {prescription?.medicationRequests?.map((medicationRequest, index) => {
                                    const programOrClinicProgram =
                                        medicationRequest?.programTemplate?.program || medicationRequest?.clinicProgram;
                                    const is_program = !!programOrClinicProgram?.id;
                                    const title =
                                        programOrClinicProgram?.title || medicationRequest.medication || '--';
                                    const qty = Number(medicationRequest?.quantity_authorized) || 1;
                                    const unitPriceCents = medicationRequest?.clinicProduct?.selling_price_cents ?? 0;
                                    // Use backend-computed total (unit × qty) when available, fall back to manual calc
                                    const totalPriceCents = medicationRequest?.selling_price_cents ?? (unitPriceCents * qty);
                                    const hasPrice = totalPriceCents > 0;
                                    const totalPrice = hasPrice ? `$${(totalPriceCents / 100).toFixed(2)}` : '--';
                                    const unitPrice = unitPriceCents > 0 ? `$${(unitPriceCents / 100).toFixed(2)}` : null;
                                    const practitioner = prescription?.practitioner?.full_name || '--';

                                    const itemDiscountCents = Number(medicationRequest?.discount_cents) || 0;
                                    const isColdChainMed = medicationRequest?.product?.storage_requirement === 'refrigerated' || medicationRequest?.product?.storage_requirement === 'frozen';

                                    if (is_program) {
                                        const program = programOrClinicProgram;
                                        return (
                                            <div key={medicationRequest?.id} className="space-y-2 border-t border-slate-100 pt-3 mt-3">
                                                <div className="flex justify-between text-sm items-baseline">
                                                    <span className="text-slate-500">Price:</span>
                                                    <div className="text-right">
                                                        <span className="font-medium text-slate-900">{totalPrice}</span>
                                                        {unitPrice && qty > 1 && (
                                                            <p className="text-xs text-slate-400">{unitPrice} × {qty}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                {itemDiscountCents > 0 && (
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-slate-500">Discount:</span>
                                                        <span className="font-medium text-green-600">-${(itemDiscountCents / 100).toFixed(2)}</span>
                                                    </div>
                                                )}
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-slate-500">Practitioner:</span>
                                                    <span className="font-medium text-slate-900 capitalize">{practitioner}</span>
                                                </div>
                                                <Accordion.Root
                                                    type="single"
                                                    collapsible
                                                    value={show}
                                                    onValueChange={setShow}
                                                    className="w-full"
                                                >
                                                    <Accordion.Item
                                                        value={medicationRequest?.id}
                                                        className="border border-slate-200 bg-white overflow-hidden rounded-lg"
                                                    >
                                                        <Accordion.Header>
                                                            <Accordion.Trigger
                                                                className={cx(
                                                                    'group flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 transition-colors duration-200',
                                                                    'focus:outline-none'
                                                                )}
                                                                title="Expand/Collapse"
                                                            >
                                                                <div className="flex items-center justify-between w-full gap-4">
                                                                    <span className="text-sm text-slate-500">Program:</span>
                                                                    <div className="flex items-center gap-2">
                                                                        <Package className="h-4 w-4 text-slate-500" />
                                                                        <span className="text-sm font-medium text-slate-900">
                                                                            {title}
                                                                        </span>
                                                                        {isColdChainMed && <Snowflake className="h-3.5 w-3.5 text-blue-500 shrink-0" title="Cold chain - requires overnight/express shipping" />}
                                                                    </div>
                                                                </div>
                                                                <ChevronDown
                                                                    className={cx(
                                                                        'h-4 w-4 text-slate-400 transition-transform duration-200',
                                                                        show === medicationRequest?.id && 'rotate-180'
                                                                    )}
                                                                />
                                                            </Accordion.Trigger>
                                                        </Accordion.Header>
                                                        <Accordion.Content className="px-3 pb-3 pt-0 text-sm text-slate-600">
                                                            <div className="flex flex-col gap-2 pt-2 border-t border-slate-100">
                                                                <div>
                                                                    <span className="text-slate-500">Subtitle: </span>
                                                                    <span>{program?.subtitle}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-slate-500">Inclusions: </span>
                                                                    <span>{program?.inclusions}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-slate-500">Target: </span>
                                                                    <span>{program?.target}</span>
                                                                </div>
                                                            </div>
                                                        </Accordion.Content>
                                                    </Accordion.Item>
                                                </Accordion.Root>
                                            </div>
                                        );
                                    }

                                    const isColdChainItem = medicationRequest?.product?.storage_requirement === 'refrigerated' || medicationRequest?.product?.storage_requirement === 'frozen';

                                    return (
                                        <div key={index} className="space-y-2 border-t border-slate-100 pt-3 mt-3">
                                            <div className="flex justify-between text-sm">
                                                <span className="text-slate-500">Program:</span>
                                                <span className="font-medium text-slate-900 text-right flex items-center gap-1 justify-end">
                                                    {isColdChainItem && <Snowflake className="h-3.5 w-3.5 text-blue-500 shrink-0" title="Cold chain - requires overnight/express shipping" />}
                                                    {title}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm items-baseline">
                                                <span className="text-slate-500">Price:</span>
                                                <div className="text-right">
                                                    <span className="font-medium text-slate-900">{totalPrice}</span>
                                                    {unitPrice && qty > 1 && (
                                                        <p className="text-xs text-slate-400">{unitPrice} × {qty}</p>
                                                    )}
                                                </div>
                                            </div>
                                            {itemDiscountCents > 0 && (
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-slate-500">Discount:</span>
                                                    <span className="font-medium text-green-600">-${(itemDiscountCents / 100).toFixed(2)}</span>
                                                </div>
                                            )}
                                            <div className="flex justify-between text-sm">
                                                <span className="text-slate-500">Practitioner:</span>
                                                <span className="font-medium text-slate-900 capitalize">{practitioner}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Shipping Selection — hidden for external fulfillment (e.g. OptioRx) */}
                            {!prescription?.is_external_fulfillment && (
                            <div className="border border-slate-200 rounded-lg p-4 space-y-4">
                                <h4 className="font-medium text-slate-900 text-sm">Shipping Options <span className="text-slate-400 font-normal">(Optional)</span></h4>
                                
                                {/* Cold Chain Shipping Banner */}
                                {requiresColdChain && (
                                    <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                        <Snowflake className="h-5 w-5 text-blue-600 flex-shrink-0" />
                                        <div>
                                            <p className="text-sm font-medium text-blue-800">
                                                Cold Chain Shipping Required
                                            </p>
                                            <p className="text-xs text-blue-600">
                                                This order contains temperature-sensitive medication. Only overnight/express options are shown.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Free Shipment Banner */}
                                {isFreeShipment && patientState && (
                                    <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                                        <Gift className="h-5 w-5 text-green-600 flex-shrink-0" />
                                        <div>
                                            <p className="text-sm font-medium text-green-800">
                                                Free Shipping Available!
                                            </p>
                                            <p className="text-xs text-green-600">
                                                Patient qualifies for free shipping to {patientState}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Carrier Selection */}
                                <div className="space-y-2">
                                    <Label className="text-sm font-medium text-slate-700">
                                        Shipping Carrier
                                    </Label>
                                    <Select
                                        value={selectedCarrierId}
                                        onValueChange={handleCarrierChange}
                                        disabled={isLoadingCarriers}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder={isLoadingCarriers ? "Loading carriers..." : "Select a carrier"} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {carriers.map(carrier => (
                                                <SelectItem key={carrier.id} value={carrier.id}>
                                                    <div className="flex items-center gap-2">
                                                        <Truck className="h-4 w-4 text-slate-500" />
                                                        {carrier.name}
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Delivery Options */}
                                {selectedCarrierId && (
                                    <div className="space-y-2">
                                        <Label className="text-sm font-medium text-slate-700">
                                            Delivery Option
                                        </Label>
                                        {isLoadingShippingOptions ? (
                                            <div className="flex items-center justify-center py-4">
                                                <Loader2 className="h-5 w-5 animate-spin text-brand-teal" />
                                                <span className="ml-2 text-sm text-slate-500">Loading options...</span>
                                            </div>
                                        ) : shippingOptions.length > 0 ? (
                                            <RadioGroup
                                                value={selectedShippingOptionId}
                                                onValueChange={(value) => handleShippingOptionChange(value)}
                                                className="space-y-2"
                                            >
                                                {shippingOptions.map(option => (
                                                    <div
                                                        key={option.id}
                                                        className={cx(
                                                            "flex items-center space-x-3 p-3 border rounded-lg transition-colors cursor-pointer",
                                                            selectedShippingOptionId === option.id
                                                                ? "border-brand-teal bg-brand-teal/5"
                                                                : "border-slate-200 hover:bg-slate-50"
                                                        )}
                                                    >
                                                        <RadioGroupItem value={option.id} id={`shipping-${option.id}`} />
                                                        <Label
                                                            htmlFor={`shipping-${option.id}`}
                                                            className="flex-1 cursor-pointer flex justify-between items-center"
                                                        >
                                                            <span className="text-sm font-medium text-slate-900">
                                                                {option.name}
                                                            </span>
                                                            <div className="flex items-center gap-2">
                                                                {isFreeShipment && option.totalCost === 0 ? (
                                                                    <>
                                                                        <span className="text-sm text-slate-400 line-through">
                                                                            ${option.originalCost.toFixed(2)}
                                                                        </span>
                                                                        <Badge variant="secondary" className="bg-green-100 text-green-700 hover:bg-green-100">
                                                                            FREE
                                                                        </Badge>
                                                                    </>
                                                                ) : (
                                                                    <span className="text-sm font-semibold text-slate-900">
                                                                        ${option.totalCost.toFixed(2)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </Label>
                                                    </div>
                                                ))}
                                            </RadioGroup>
                                        ) : shippingError ? (
                                            <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                                <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-sm text-red-700">{shippingError}</span>
                                                    {shouldShowAddressLink && patientOverviewPath && (
                                                        <a
                                                            href={patientOverviewPath}
                                                            className="text-sm font-medium text-red-700 underline hover:text-red-800"
                                                        >
                                                            View patient address
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-sm text-slate-500 py-2">
                                                No delivery options available for this carrier
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Total Amount Preview */}
                                {selectedShippingOptionId && (
                                    <div className="pt-3 border-t border-slate-100">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Medication:</span>
                                            <span className="text-slate-900">${(subtotalBeforeDiscountCents / 100).toFixed(2)}</span>
                                        </div>
                                        {totalDiscountCents > 0 && (
                                            <div className="flex justify-between text-sm mt-1">
                                                <span className="text-slate-500">Discount:</span>
                                                <span className="font-medium text-green-600">-${(totalDiscountCents / 100).toFixed(2)}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between text-sm mt-1">
                                            <span className="text-slate-500">Shipping:</span>
                                            {isFreeShipment ? (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-slate-400 line-through text-xs">
                                                        ${(shippingOptions.find(o => o.id === selectedShippingOptionId)?.originalCost || 0).toFixed(2)}
                                                    </span>
                                                    <span className="text-green-600 font-medium">$0.00</span>
                                                </div>
                                            ) : (
                                                <span className="text-slate-900">
                                                    ${(shippingOptions.find(o => o.id === selectedShippingOptionId)?.totalCost || 0).toFixed(2)}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex justify-between font-semibold mt-2 pt-2 border-t border-slate-100">
                                            <span className="text-slate-900">Total:</span>
                                            <span className="text-slate-900">${(totalAmount / 100).toFixed(2)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            )}

                            {/* Payment Requirements - Show what's missing before payment can proceed */}
                            {!prescription?.is_external_fulfillment && !isError && (!selectedCarrierId || !selectedShippingOptionId) && (
                                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                    <div className="flex items-start gap-2 mb-2">
                                        <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                        <span className="text-sm font-medium text-amber-800">
                                            Complete the following to send payment link:
                                        </span>
                                    </div>
                                    <ul className="ml-6 space-y-1">
                                        {!selectedCarrierId && (
                                            <li className="text-sm text-amber-700 flex items-center gap-2">
                                                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full flex-shrink-0" />
                                                Select a shipping carrier
                                            </li>
                                        )}
                                        {selectedCarrierId && !selectedShippingOptionId && (
                                            <li className="text-sm text-amber-700 flex items-center gap-2">
                                                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full flex-shrink-0" />
                                                Select a delivery option
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            )}

                            {/* Error Messages */}
                            {isError && (
                                <div className="space-y-2">
                                    {errorMessages.map((errorMessage, index) => (
                                        <div key={index} className="flex items-start space-x-2 p-3 bg-red-50 border border-red-100 rounded-lg">
                                            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                                            <span className="text-sm text-red-700">{errorMessage}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* COMMENTED: "I understand and want to proceed with sending the payment link" override - blocked when selling price <= base price */}
                            {/* {hasPriceWarning && (
                                <div className="flex flex-col gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                        <span className="text-sm text-amber-800">
                                            One or more medications have a patient price less than or equal to the pharmacy cost. The clinic may lose money or have no margin on this order. Please review pricing in Settings → Products before proceeding.
                                        </span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="price-warning-ack-send"
                                            checked={priceWarningAcknowledged}
                                            onCheckedChange={(checked) => setPriceWarningAcknowledged(!!checked)}
                                        />
                                        <Label
                                            htmlFor="price-warning-ack-send"
                                            className="text-sm font-medium text-amber-800 cursor-pointer"
                                        >
                                            I understand and want to proceed with sending the payment link
                                        </Label>
                                    </div>
                                </div>
                            )} */}
                        </div>
                    )}
                </DialogBody>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleClose}
                        disabled={isLoading || isSending}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={isLoading || isSending || isError || !onboardingComplete || (!getDefaultContactValue(prescription?.patient?.contacts, 'email') && !customEmail.trim())}
                        className="bg-brand-teal hover:bg-brand-teal/90"
                    >
                        {isSending ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Sending...
                            </>
                        ) : (
                            <>
                                <Send className="mr-2 h-4 w-4" />
                                Send Payment Link
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        {/* Edit Medications Dialog */}
        {prescription && (
            <EditMedicationsDialog
                open={isEditMedicationsOpen}
                onOpenChange={setIsEditMedicationsOpen}
                prescription={prescription}
                onMedicationsUpdated={() => {
                    fetchPrescription(true); // Silent refetch - don't show loading state
                    onPrescriptionUpdated?.(); // Notify parent to refetch list
                }}
            />
        )}
        </>
    );
};

export default SendPaymentLinkDialog;
